/*
 * Copyright (c) 2019.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Vladimir Ganturin <gun2rin@gmail.com>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {Environment, HTTP_STATUS} from 'compob2b-libs';
import {BehaviorSubject, Observable, throwError} from 'rxjs';
import {catchError, filter, finalize, switchMap, take} from 'rxjs/operators';
import {AuthService} from '../services/auth.service';

/**
 * Globally intercepts requests to backend
 * and adds Authorization header
 * @see {@link https://medium.com/angular-in-depth/top-10-ways-to-use-interceptors-in-angular-db450f8a62d6}
 * @see {@link https://angular-academy.com/angular-jwt/}
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );

  constructor(private readonly auth: AuthService,
              @Inject('environment') private readonly environment: Environment) {
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    /**
     * Get the auth token from the service.
     */
    const authToken = this.auth.getToken();

    /**
     * Clone the request and replace the original headers with
     * cloned headers, updated with the authorization.
     */

    if (authToken) {
      const authReq = this.addToken(req, authToken);

      /**
       * send cloned request with header to the next handler.
       */

      return next
        .handle(authReq)
        .pipe(catchError(error => this.handleError(req, error, next)));
    } else {
      return next.handle(req);
    }
  }

  /**
   * @param request Current request
   * @param token JWT
   */
  private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    const ignoreToken = request.headers.get('X-IGNORE-TOKEN') || 'no';
    if (ignoreToken === 'yes') {
      return request.clone({headers: request.headers.delete('X-IGNORE-TOKEN', 'yes')});
    }

    return request.clone({
      headers: request.headers.set(
        'Authorization',
        this.environment.tokenName + ' ' + token
      ),
    });
  }

  /**
   * Handling errors with refreshing request
   * @param request current request
   * @param error given error
   * @param next HttpHandler
   */
  private handleError(
    request: HttpRequest<any>,
    error: HttpErrorResponse,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (error.headers?.get('Compo-Info') === 'BAD_REFRESH' || error.headers?.get('Compo-Refresh') === 'BAD_REFRESH') {
      this.auth.logout();
      return throwError(() => error);
    }

    // BL-011: рефреш на 401, А ТАКЖЕ на 403 С ИСТЁКШИМ access-токеном. Бэк отдаёт 403 и на
    // протухший токен (анонимный запрос → дефолтный Http403ForbiddenEntryPoint), и на «нет прав» —
    // по ОТВЕТУ они неразличимы, различаем по КЛИЕНТСКОМУ токену:
    //  • протух (isAccessTokenExpired) → рефреш-ветка ниже (прозрачный авто-рефреш; если refresh-токен
    //    тоже мёртв → refreshToken() сам делает logout()+throw → /sign-in, легитимный релогин);
    //  • «нет прав» = токен ВАЛИДЕН (не истёк) → expired-ветка НЕ срабатывает → 403 пробрасываем как
    //    ошибку прав (CompoErrorHandler поднимет forbidden-флаг, БЕЗ logout/редиректа) — loop-fix цел.
    const isAuthError = error instanceof HttpErrorResponse
      && (error.status === HTTP_STATUS.UNAUTHORIZED
        || (error.status === HTTP_STATUS.FORBIDDEN && this.auth.isAccessTokenExpired()));
    if (!isAuthError) {
      return throwError(() => error);
    }

    /**
     * Первый 401 в цикле — запускаем refresh; остальные конкурентные запросы
     * ждут его результата через refreshTokenSubject (см. ветку ниже).
     */
    if (!this.auth.getRefreshing()) {
      this.auth.setRefreshing(true);
      this.refreshTokenSubject.next(null);

      return this.auth.refreshToken().pipe(
        switchMap((response) => {
          // refreshToken вернул Error-значение (а не бросил) — пробрасываем его в
          // единый catchError ниже, чтобы logout жил в одном месте.
          if (response instanceof Error) {
            return throwError(() => response);
          }
          const newToken = response.data.access.token;
          this.refreshTokenSubject.next(newToken);
          return next.handle(this.addToken(request, newToken));
        }),
        // Любой провал refresh (бросок ИЛИ Error-значение выше) → logout + проброс.
        // Запрос не вешаем: logout уводит на sign-in и отменяет висящие подписки.
        catchError((refreshErr) => {
          this.auth.logout();
          return throwError(() => refreshErr);
        }),
        // ВСЕГДА сбрасываем флаг — раньше setRefreshing(false) стоял только в success-ветке,
        // и при ошибке refresh флаг залипал в true: все последующие запросы вкладки уходили
        // в ветку ожидания ниже и зависали/падали (чинилось только перезагрузкой вкладки).
        finalize(() => this.auth.setRefreshing(false)),
      );
    }

    /**
     * Refresh уже идёт — ждём новый токен (next() в success-ветке выше) и повторяем
     * запрос с ним. При провале refresh logout() уводит на sign-in и отменяет ожидание.
     */
    return this.refreshTokenSubject.pipe(
      filter(token => token != null),
      take(1),
      switchMap((token: string) => next.handle(this.addToken(request, token))),
    );
  }
}
